import { css, html, LitElement, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { WorkCardImageAspectRatio, WorkCardMediaStyle } from './types'

@safeCustomElement('mdc-work-card')
export class MdcWorkCard extends LitElement {
  @property({ type: String, attribute: 'media-style' }) mediaStyle: WorkCardMediaStyle =
    'square-rounded-corners'
  @property({ type: String, attribute: 'image-aspect-ratio' })
  imageAspectRatio: WorkCardImageAspectRatio = 'auto'
  @property({ type: String, attribute: 'image-src' }) imageSrc = ''
  @property({ type: String, attribute: 'image-alt' }) imageAlt = ''
  @property({ type: String }) href = ''
  @property({ type: Boolean, attribute: 'is-video' }) isVideo = true
  @property({ type: String, attribute: 'client-name' }) clientName = ''
  @property({ type: String, attribute: 'campaign-type' }) campaignType = ''
  @property({ type: String, attribute: 'campaign-name' }) campaignName = ''
  @property({ type: String }) views = ''

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-work-card {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-050, 0.5rem);
			text-decoration: none;
			color: inherit;
		}

		.mdc-work-card__media {
			display: flex;
			position: relative;
		}

		.mdc-work-card__media--square .mdc-work-card__image {
			border-radius: 0;
		}

		.mdc-work-card__media--square-rounded-corners .mdc-work-card__image {
			border-radius: var(--radius-md, 0.5rem);
		}

		.mdc-work-card__media--aspect-square {
			aspect-ratio: 1;
			overflow: hidden;
		}

		.mdc-work-card__media--aspect-square .mdc-work-card__image {
			height: 100%;
		}

		.mdc-work-card__image {
			box-sizing: border-box;
			max-width: 100%;
			width: 100%;
			object-fit: cover;
			border: 1px solid var(--color-border-primary, var(--color-functional-gray-200));
		}

		.mdc-work-card__play-icon {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.mdc-work-card__content {
			display: flex;
			flex-direction: column;
			gap: 0.375rem;
		}

		.mdc-work-card__title {
			color: var(--color-foreground-interactive-primary-default, var(--color-functional-gray-black));
			font-size: var(--typography-font-size-100, 1rem);
			font-family: var(--typography-font-family-sans);
			font-weight: 600;
			letter-spacing: -0.01em;
			line-height: 1.25rem;
			transition: color 0.3s;
			margin: 0;
			padding: 0;
			text-overflow: ellipsis;
		}

		.mdc-work-card__subtitles {
			display: flex;
			flex-direction: column;
			line-height: 1.25rem;
		}

		.mdc-work-card__subtitle {
			overflow: hidden;
			color: var(--color-foreground-secondary, var(--color-functional-gray-300));
			text-overflow: ellipsis;
			max-width: 100%;
			white-space: nowrap;
			font-size: var(--typography-font-size-075);
			font-weight: 450;
			margin: 0;
			padding: 0;
		}

		.mdc-work-card__subtitle--type {
			text-transform: capitalize;
		}

		.mdc-work-card__subtitle--name {
		}

		/* breakpoints.md */
		@media (min-width: 768px) {
			.mdc-work-card {
				gap: var(--spacing-075, 0.75rem);
			}

			.mdc-work-card__subtitle {
				line-height: 1.3;
			}
		}
	`

  render() {
    const mediaClassName = `mdc-work-card__media mdc-work-card__media--${this.mediaStyle} mdc-work-card__media--aspect-${this.imageAspectRatio}`

    return html`
			<a href=${this.href} class="mdc-work-card">
				<div class=${mediaClassName}>
					<img src=${this.imageSrc} alt=${this.imageAlt} class="mdc-work-card__image" loading="lazy" />
					${
            this.isVideo
              ? html`
								<div class="mdc-work-card__play-icon">
									<mdc-icon-button type="tertiary" size="md" iconSize="smaller" icon="play-filled"></mdc-icon-button>
								</div>
							`
              : nothing
          }
				</div>
				<div class="mdc-work-card__content">
					<p class="mdc-work-card__title">${this.campaignName}</p>
					<div class="mdc-work-card__subtitles">
						${this.campaignType ? html`<p class="mdc-work-card__subtitle mdc-work-card__subtitle--type">${this.campaignType}</p>` : nothing}
						${this.clientName ? html`<p class="mdc-work-card__subtitle mdc-work-card__subtitle--name">${this.clientName}</p>` : nothing}
						${this.views ? html`<p class="mdc-work-card__subtitle mdc-work-card__subtitle--views">${this.views}</p>` : nothing}
					</div>
				</div>
			</a>
		`
  }
}
